import React, { ReactNode, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { Mention } from 'modules/community/components/editor/elements/mention/mention'
import { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useHighlightSearchContext } from 'modules/community/context/highlight-search'
import { getTextAroundWord } from 'modules/community/utils/get-text-around-word'

const MENTION_TOKEN_RE = /<@(\d+):([^>]+)>/g

const escapeAttr = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')

export const parseTextWithMentions = (text: string, mentions: MentionInterface[]): string => {
  const byId = new Map((mentions ?? []).map(m => [m.userId, m]))

  return text.replaceAll(MENTION_TOKEN_RE, (_raw, idStr: string, displayName: string) => {
    const userId = Number(idStr)
    const name = escapeAttr(displayName)
    const profileImage = escapeAttr(byId.get(userId)?.profileImage ?? '')
    const dataAvatarUrl = profileImage ? ` data-avatar-url="${profileImage}"` : ''
    return `<span data-type="mention" contenteditable="false"${dataAvatarUrl} data-cy="mention-${name}" data-id="${userId}">@${name}</span>`
  })
}

interface TextWithMentionsProps extends React.HTMLProps<HTMLDivElement> {
  text: string
  mentions: MentionInterface[]
  truncateLength?: number
}

export const TextWithMentions = ({
  text,
  mentions,
  truncateLength,
  ...rest
}: TextWithMentionsProps): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null)
  const highlightContext = useHighlightSearchContext()

  useEffect(() => {
    if (containerRef.current) {
      let finalHtml = parseTextWithMentions(text, mentions)
      if (truncateLength) {
        if (highlightContext?.highlightKeyword) {
          finalHtml = highlightContext.wrapWithHighlight(finalHtml, truncateLength)
        } else {
          const measure = document.createElement('div')
          measure.innerHTML = finalHtml
          const textLen = measure.textContent?.length ?? 0
          if (textLen > truncateLength) {
            finalHtml = getTextAroundWord(finalHtml, truncateLength)
          }
        }
      }

      containerRef.current.innerHTML = finalHtml

      const mentionElements =
        containerRef.current.querySelectorAll<HTMLSpanElement>('[data-type="mention"]')
      mentionElements?.forEach(element => {
        const id = element.dataset.id
        const displayName = element.dataset.cy?.split('mention-').pop()
        const profileImageUrl = element.dataset.avatarUrl

        const root = createRoot(element)
        root.render(
          <Mention
            element={{
              displayName: displayName || '',
              id: Number(id) || 0,
              profileImageUrl: profileImageUrl || '',
              type: 'mention',
              children: [{ text: '' }],
            }}
          />,
        )
      })
    }
  }, [
    text,
    mentions,
    truncateLength,
    highlightContext?.highlightKeyword,
    highlightContext?.wrapWithHighlight,
  ])

  return <div ref={containerRef} {...rest} />
}
